<template>
  <!-- Modern Inbox Container -->
  <div class="inbox-page-container">
    <!-- Subtle gradient background -->
    <div class="background-gradient"></div>

    <div class="inbox-layout flex flex-col lg:flex-row relative z-10">
      <!-- Main Content Area -->
      <main
        class="main-content flex-1 flex flex-col lg:flex-row h-[100dvh] lg:h-screen relative bg-muted/20"
      >

        <!-- Conversations List Panel -->
        <div
          class="conversations-panel w-full lg:w-96 xl:w-[28rem] bg-card border-r border-border flex flex-col h-[100dvh] lg:h-screen"
          :class="{
            hidden: (isSmallScreen || isTablet) && showThreadPanel,
            block: !isSmallScreen || !showThreadPanel,
          }"
        >
          <!-- Panel Header -->
          <div
            class="panel-header p-6 pb-4 border-b border-border bg-card flex-shrink-0"
          >
            <h1 class="text-2xl font-bold text-foreground mb-1">Messages</h1>
            <p class="text-sm text-muted-foreground">
              {{ filteredAndSearchedThreads.length }} conversation{{
                filteredAndSearchedThreads.length !== 1 ? "s" : ""
              }}
            </p>
          </div>

          <!-- Conversations List -->
          <div
            ref="conversationsListRef"
            class="conversations-list overflow-y-auto flex-1 min-h-0"
            @scroll="handleOljScroll"
          >
            <div
              v-if="
                loading ||
                (filter === 'olj' && oljMessagesLoading)
              "
              class="flex items-center justify-center p-12"
            >
              <div class="flex flex-col items-center">
                <div
                  class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
                ></div>
                <p class="mt-4 text-sm text-muted-foreground">
                  {{
                    filter === "olj"
                      ? "Loading messages..."
                      : "Loading conversations..."
                  }}
                </p>
              </div>
            </div>

            <div v-else>
              <div
                v-if="filteredAndSearchedThreads.length === 0"
                class="empty-state p-12 text-center"
              >
                <div class="empty-icon mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-16 w-16 mx-auto text-muted-foreground/30"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                </div>
                <h3 class="text-lg font-semibold text-foreground mb-2">
                  No conversations found
                </h3>
                <p class="text-sm text-muted-foreground">
                  {{ getEmptyStateMessage() }}
                </p>
              </div>

              <!-- Thread Cards -->
              <div class="thread-list">
                <div
                  v-for="thread in filteredAndSearchedThreads"
                  :key="thread.id"
                  @click="selectThread(thread.id)"
                  class="thread-card p-4 border-b border-border cursor-pointer transition-all duration-200 hover:bg-muted/30"
                  :class="{
                    'bg-primary/5 border-l-4 border-l-primary':
                      selectedThreadId === thread.id ||
                      (thread.source === 'olj' && isOljThreadSelected(thread)),
                    'bg-red-500/10':
                      (thread.source === 'olj' && thread.olj_unread) ||
                      (thread.last_message &&
                        !thread.last_message.is_read &&
                        thread.last_message.sender !== userId),
                  }"
                >
                  <div class="flex items-start gap-3">
                    <!-- Avatar -->
                    <div class="avatar-container relative flex-shrink-0">
                      <div
                        v-if="isAdminParticipant(thread)"
                        class="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden bg-primary"
                      >
                        <img
                          src="/logo/applypa-logo.png"
                          alt="Admin"
                          class="w-full h-full object-cover"
                        />
                      </div>
                      <div
                        v-else
                        class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 flex items-center justify-center text-lg font-bold text-white"
                      >
                        {{ getOtherParticipantInitial(thread) }}
                      </div>
                      <div
                        v-if="
                          (thread.source === 'olj' && thread.olj_unread) ||
                          (thread.last_message &&
                            !thread.last_message.is_read &&
                            thread.last_message.sender !== userId)
                        "
                        class="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-card"
                      ></div>
                    </div>

                    <!-- Thread Info -->
                    <div class="flex-1 min-w-0">
                      <div class="flex items-start justify-between gap-2 mb-1">
                        <h3
                          class="thread-title font-semibold text-foreground text-sm truncate"
                        >
                          <span
                            class="thread-title-text"
                            v-html="
                              highlightSearchMatch(
                                thread.source === 'olj' && thread.olj_partner
                                  ? thread.olj_partner.name ||
                                      thread.olj_partner.business_name ||
                                      'Employer'
                                  : isAdminParticipant(thread)
                                  ? 'Admin'
                                  : getOtherParticipantName(thread)
                              )
                            "
                          ></span>
                        </h3>
                        <span
                          class="thread-time text-xs text-muted-foreground whitespace-nowrap flex-shrink-0"
                          v-if="
                            thread.last_message ||
                            thread.olj_latest_message_time_ago
                          "
                        >
                          {{
                            thread.olj_latest_message_time_ago ||
                            (thread.last_message
                              ? formatThreadDate(thread.last_message.sent_at)
                              : "")
                          }}
                        </span>
                      </div>

                      <div class="flex items-center gap-2 mb-1">
                        <p
                          class="thread-preview text-sm text-muted-foreground truncate flex-1"
                        >
                          <span
                            v-if="thread.last_message"
                            class="font-medium text-foreground"
                          >
                            {{
                              thread.source === "olj"
                                ? thread.olj_partner?.name ||
                                  thread.olj_partner?.business_name ||
                                  "Employer"
                                : isMessageFromAdmin(thread.last_message)
                                ? "Admin"
                                : thread.last_message.sender_name
                            }}:
                          </span>
                          <span
                            v-if="thread.last_message"
                            class="preview-text"
                            v-html="
                              highlightSearchMatch(
                                thread.source === 'olj' && thread.olj_thread
                                  ? thread.olj_thread.subject ||
                                      thread.last_message.body
                                  : thread.last_message.body
                              )
                            "
                          ></span>
                          <span
                            v-else-if="thread.olj_thread"
                            class="preview-text"
                          >
                            {{ thread.olj_thread.subject }}
                          </span>
                        </p>
                      </div>

                      <div v-if="isArchivedThread(thread)" class="mt-1">
                        <span
                          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-700 dark:text-amber-300"
                        >
                          <svg
                            class="w-3 h-3 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                            <path
                              fill-rule="evenodd"
                              d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                              clip-rule="evenodd"
                            />
                          </svg>
                          Archived
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Loading More Indicator for OLJ Messages -->
              <div
                v-if="
                  oljMessagesLoadingMore &&
                  filter === 'olj'
                "
                class="flex items-center justify-center p-8"
              >
                <div class="flex flex-col items-center">
                  <div
                    class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"
                  ></div>
                  <p class="mt-2 text-sm text-muted-foreground">
                    Loading more messages...
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Message Thread Panel -->
        <div
          class="thread-panel flex-1 bg-background h-[100dvh] lg:h-screen flex flex-col overflow-hidden"
          :class="{
            hidden: (isSmallScreen || isTablet) && !showThreadPanel,
          }"
        >
          <template v-if="selectedThreadId || selectedOljHashId">
            <!-- Mobile Back Button -->
            <div
              v-if="isSmallScreen || isTablet"
              class="mobile-header bg-card border-b border-border p-4 flex items-center gap-3 sticky top-0 z-40"
            >
              <button
                @click="showThreadPanel = false"
                class="back-btn p-2 rounded-lg text-muted-foreground hover:bg-muted/50 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <span class="font-medium text-foreground">Back</span>
            </div>
            <!-- Regular Message Thread -->
            <MessageThread
              v-if="selectedThreadId"
              :thread-id="selectedThreadId"
            />
            <!-- OLJ Thread Display -->
            <OljThreadDisplay
              v-else-if="selectedOljHashId"
              :thread-data="oljThreadData"
              :loading="oljThreadLoading"
              :hash-id="selectedOljHashId"
              :partner-data="selectedOljPartner"
            />
          </template>

          <!-- Empty State -->
          <div
            v-else
            class="empty-thread-state h-full flex flex-col items-center justify-center text-center p-12"
          >
            <div class="empty-icon mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-16 w-16 text-muted-foreground/30"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-foreground mb-2">
              Select a conversation
            </h3>
            <p class="text-sm text-muted-foreground max-w-sm">
              Choose a conversation from the list to start messaging
            </p>
          </div>
        </div>
      </main>
    </div>
    <!-- New Thread Modal -->
    <transition name="modal-fade">
      <div
        v-if="showNewThreadModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-md"
      >
        <div
          class="modal-content max-w-2xl w-full mx-4 relative animate-fadeIn rounded-3xl border border-primary/20 shadow-2xl modal-responsive bg-card overflow-hidden"
        >
          <!-- Modal Header with gradient -->
          <div
            class="modal-header bg-primary px-8 py-6 flex justify-between items-center"
          >
            <h2 class="text-2xl md:text-3xl font-bold text-white">
              Start New Conversation
            </h2>
            <button
              @click="showNewThreadModal = false"
              class="modal-close text-primary-foreground hover:text-primary-foreground/70 focus:outline-none transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div class="modal-body p-6 md:p-8">
            <form @submit.prevent="createNewThread">
              <!-- Admin Section -->
              <div class="mb-6 md:mb-8">
                <label
                  class="block text-base md:text-lg font-semibold mb-3 text-foreground"
                  >Admin</label
                >
                <div
                  class="flex items-center gap-4 bg-background rounded-xl p-4 md:p-5 border border-border shadow-sm"
                >
                  <div
                    class="avatar-container w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center text-white text-xl md:text-2xl font-bold border-2 border-white shadow-lg"
                  >
                    <img
                      src="/logo/applypa-logo.png"
                      alt="Admin"
                      class="w-full h-full object-cover rounded-full"
                    />
                  </div>
                  <div class="flex flex-col">
                    <span class="font-bold text-foreground text-lg md:text-xl"
                      >Admin</span
                    >
                    <span class="text-muted-foreground text-sm md:text-base">{{
                      adminDisplayEmail
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- Jobseeker Selection -->
              <div class="mb-6 md:mb-8">
                <label
                  class="block text-base md:text-lg font-semibold mb-3 text-foreground"
                  >Select Jobseeker</label
                >
                <div class="relative">
                  <!-- Styled Dropdown -->
                  <div class="relative">
                    <select
                      v-model="selectedJobseeker"
                      class="w-full p-4 md:p-5 rounded-xl border-2 border-border focus:border-primary focus:ring-2 focus:ring-primary/40 text-base md:text-lg bg-background text-foreground font-medium shadow-sm appearance-none"
                    >
                      <option value="" disabled>Select a jobseeker...</option>
                      <option
                        v-for="js in jobseekerList"
                        :key="js.id"
                        :value="js.id"
                      >
                        {{ js.name }}
                      </option>
                    </select>
                    <!-- Custom dropdown arrow -->
                    <div
                      class="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none"
                    >
                      <svg
                        class="w-5 h-5 text-primary"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fill-rule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        ></path>
                      </svg>
                    </div>
                  </div>

                  <!-- Empty state message -->
                  <div
                    v-if="jobseekerList.length === 0"
                    class="mt-3 bg-background text-foreground rounded-xl shadow p-4 text-center border border-border"
                  >
                    <span class="flex items-center justify-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5 text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      No jobseekers available for a new conversation.
                    </span>
                  </div>
                </div>

                <!-- Selected Jobseeker Preview -->
                <div
                  v-if="selectedJobseekerObj"
                  class="mt-4 p-4 md:p-5 rounded-xl bg-background border border-border flex items-start gap-4 shadow-sm animate-fadeIn"
                >
                  <div
                    class="avatar-container w-12 h-12 rounded-full bg-gradient-to-br from-green-400 via-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold border-2 border-white shadow-lg"
                  >
                    {{ selectedJobseekerObj.name.charAt(0).toUpperCase() }}
                  </div>
                  <div class="flex flex-col flex-1">
                    <span class="font-bold text-foreground text-lg">{{
                      selectedJobseekerObj.name
                    }}</span>
                    <span class="text-muted-foreground text-sm md:text-base">{{
                      selectedJobseekerObj.email
                    }}</span>
                    <div class="flex items-center gap-2 mt-1">
                      <span
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="
                          selectedJobseekerObj.onboarding_completed
                            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30'
                            : 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30'
                        "
                      >
                        <span
                          class="w-2 h-2 rounded-full mr-1.5"
                          :class="
                            selectedJobseekerObj.onboarding_completed
                              ? 'bg-green-500'
                              : 'bg-yellow-500'
                          "
                        ></span>
                        {{
                          selectedJobseekerObj.onboarding_completed
                            ? "Onboarded"
                            : "Not onboarded"
                        }}
                      </span>
                      <span class="text-xs text-muted-foreground"
                        >Joined:
                        {{ formatDate(selectedJobseekerObj.date_joined) }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div
                class="flex flex-col sm:flex-row justify-end mt-6 md:mt-8 gap-3"
              >
                <button
                  type="button"
                  @click="showNewThreadModal = false"
                  class="px-6 py-3 rounded-xl bg-background text-foreground font-semibold hover:bg-muted/50 transition-all w-full sm:w-auto text-base md:text-lg border border-border shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="creatingThread || !selectedJobseeker"
                  class="px-6 py-3 rounded-xl font-bold transition-all w-full sm:w-auto text-base md:text-lg shadow-lg"
                  :class="
                    selectedJobseeker
                      ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground cursor-not-allowed'
                  "
                >
                  <span
                    v-if="creatingThread"
                    class="flex items-center justify-center gap-2"
                  >
                    <svg
                      class="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Creating...
                  </span>
                  <span v-else>Create Conversation</span>
                </button>
              </div>

              <!-- Error Message -->
              <div
                v-if="threadError"
                class="mt-5 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-700 dark:text-red-300 text-center animate-fadeIn"
              >
                <div class="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {{ threadError }}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </transition>

    <!-- Message Blast Modal -->
    <transition name="modal-fade">
      <div
        v-if="showMessageBlastModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-md"
      >
        <div
          class="modal-content max-w-xl w-full mx-4 relative animate-fadeIn rounded-3xl border border-border shadow-2xl modal-responsive bg-card overflow-hidden"
        >
          <!-- Modal Header with gradient -->
          <div
            class="modal-header bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4 flex justify-between items-center"
          >
            <h2 class="text-xl font-bold text-white">📢 Message Blast</h2>
            <button
              @click="showMessageBlastModal = false"
              class="modal-close text-primary-foreground hover:text-primary-foreground/70 focus:outline-none transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div class="modal-body p-4">
            <form @submit.prevent="sendMessageBlast">
              <!-- User Selection Section -->
              <div class="mb-4">
                <div class="flex items-center justify-between mb-3">
                  <label class="block text-sm font-semibold text-foreground"
                    >Select Recipients</label
                  >
                  <div class="flex gap-2">
                    <button
                      type="button"
                      @click="selectAllUsers"
                      class="px-4 py-2 text-sm bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
                    >
                      Select All ({{ allUsers.length }})
                    </button>
                    <button
                      type="button"
                      @click="clearAllUsers"
                      class="px-4 py-2 text-sm bg-muted/50 text-foreground rounded-lg hover:bg-muted/70 transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <!-- User List with Search -->
                <div class="mb-3">
                  <input
                    type="text"
                    v-model="userSearchQuery"
                    placeholder="Search users..."
                    class="w-full px-3 py-2 bg-background rounded-lg border border-border focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-transparent text-sm text-foreground"
                  />
                </div>

                <!-- User Selection List -->
                <div
                  class="max-h-32 overflow-y-auto bg-background rounded-lg border border-border p-3"
                >
                  <div v-if="loadingUsers" class="text-center py-8">
                    <div
                      class="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"
                    ></div>
                    <p class="text-primary mt-2">Loading users...</p>
                  </div>
                  <div
                    v-else-if="filteredUsers.length === 0"
                    class="text-center py-8 text-muted-foreground"
                  >
                    No users found
                  </div>
                  <div v-else class="space-y-1">
                    <div
                      v-for="user in filteredUsers"
                      :key="user.id"
                      class="flex items-center p-2 rounded hover:bg-muted/50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        :id="`user-${user.id}`"
                        v-model="selectedUserIds"
                        :value="user.id"
                        class="h-3 w-3 text-primary focus:ring-primary border-border rounded"
                      />
                      <label
                        :for="`user-${user.id}`"
                        class="ml-2 flex-1 cursor-pointer"
                      >
                        <div class="flex items-center justify-between">
                          <div>
                            <p class="text-sm font-medium text-foreground">
                              {{ user.name }}
                            </p>
                            <p class="text-xs text-muted-foreground">
                              {{ user.email }}
                            </p>
                          </div>
                          <div class="text-right">
                            <span
                              class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium"
                              :class="
                                user.account_type === 'jobseeker'
                                  ? 'bg-primary/10 text-primary'
                                  : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                              "
                            >
                              {{ user.account_type }}
                            </span>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <div class="mt-2 text-xs text-primary">
                  Selected: {{ selectedUserIds.length }} users
                </div>
              </div>

              <!-- Message Composition -->
              <div class="mb-4">
                <label class="block text-sm font-semibold mb-2 text-foreground"
                  >Message</label
                >
                <textarea
                  v-model="blastMessage"
                  placeholder="Type your message blast here..."
                  rows="4"
                  class="w-full px-3 py-2 bg-background rounded-lg border border-border focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-transparent resize-none text-sm text-foreground"
                ></textarea>
                <div class="mt-1 text-xs text-muted-foreground">
                  {{ blastMessage.length }} characters
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="flex flex-col sm:flex-row justify-end mt-4 gap-2">
                <button
                  type="button"
                  @click="showMessageBlastModal = false"
                  class="px-4 py-2 rounded-lg bg-background text-foreground font-semibold hover:bg-muted/50 transition-all w-full sm:w-auto text-sm border border-border shadow-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="
                    selectedUserIds.length === 0 ||
                    !blastMessage.trim() ||
                    sendingBlast
                  "
                  class="px-4 py-2 rounded-lg font-semibold transition-all w-full sm:w-auto text-sm shadow-lg"
                  :class="
                    selectedUserIds.length > 0 &&
                    blastMessage.trim() &&
                    !sendingBlast
                      ? 'bg-primary hover:bg-primary/90 text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground cursor-not-allowed'
                  "
                >
                  <span
                    v-if="sendingBlast"
                    class="flex items-center justify-center gap-1"
                  >
                    <svg
                      class="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      ></circle>
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Sending...
                  </span>
                  <span v-else>Send to {{ selectedUserIds.length }} Users</span>
                </button>
              </div>

              <!-- Success Message -->
              <div
                v-if="blastSuccess"
                class="mt-3 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-700 dark:text-emerald-300 text-center animate-fadeIn"
              >
                <div class="flex items-center justify-center gap-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span class="text-sm">{{ blastSuccess }}</span>
                </div>
              </div>

              <!-- Error Message -->
              <div
                v-if="blastError"
                class="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-700 dark:text-red-300 text-center animate-fadeIn"
              >
                <div class="flex items-center justify-center gap-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span class="text-sm">{{ blastError }}</span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, h } from "vue";
import { useRouter } from "vue-router";
import apiClient from "@/services/api";
import authStore from "@/store/auth";
import MessageThread from "@/components/MessageThread.vue";
import OljThreadDisplay from "@/components/OljThreadDisplay.vue";

export default {
  name: "InboxPage",
  components: {
    MessageThread,
    OljThreadDisplay,
  },
  setup() {
    const router = useRouter();
    const threads = ref([]);
    const loading = ref(true);
    const filter = ref("all");
    const showCompose = ref(false);
    const composeBody = ref("");
    const isJobseeker = computed(
      () => authStore.state.user?.account_type === "jobseeker"
    );
    const searchQuery = ref("");
    const mobileMenuOpen = ref(false);

    const selectedThreadId = ref(null);
    const showThreadPanel = ref(false);
    const isSmallScreen = ref(window.innerWidth <= 599);
    const isTablet = ref(window.innerWidth > 599 && window.innerWidth <= 1024);
    const userId = computed(() => authStore.state.user?.id);
    const isAdmin = computed(
      () => authStore.state.user?.account_type === "admin"
    );
    const oljMessages = ref([]);
    const oljMessagesLoading = ref(false);
    const oljMessagesLoadingMore = ref(false);
    const oljMessagesPage = ref(1);
    const oljMessagesPerPage = ref(20);
    const oljMessagesMeta = ref(null);
    const oljMessagesLinks = ref(null);
    const conversationsListRef = ref(null);
    const oljThreadData = ref(null);
    const oljThreadLoading = ref(false);
    const selectedOljHashId = ref(null);
    const selectedOljPartner = ref(null);
    // OLJ access (olj_jobs_enabled). Users without it get no OLJ tab or OLJ calls.
    const oljUnlocked = computed(() => !!authStore.state.user?.olj_jobs_enabled);

    // Filter options with icons
    const filterOptions = computed(() => [
      {
        key: "all",
        label: "All",
        icon: () =>
          h(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 20 20",
              fill: "currentColor",
            },
            [
              h("path", {
                "fill-rule": "evenodd",
                d: "M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z",
                "clip-rule": "evenodd",
              }),
            ]
          ),
      },
      {
        key: "unread",
        label: "Unread",
        icon: () =>
          h(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 20 20",
              fill: "currentColor",
            },
            [
              h("path", {
                d: "M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z",
              }),
              h("path", {
                d: "M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z",
              }),
            ]
          ),
      },
      {
        key: "sent",
        label: "Sent",
        icon: () =>
          h(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 20 20",
              fill: "currentColor",
            },
            [
              h("path", {
                d: "M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z",
              }),
            ]
          ),
      },
      {
        key: "archived",
        label: "Archive",
        icon: () =>
          h(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 20 20",
              fill: "currentColor",
            },
            [
              h("path", { d: "M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" }),
              h("path", {
                "fill-rule": "evenodd",
                d: "M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z",
                "clip-rule": "evenodd",
              }),
            ]
          ),
      },
      // OLJ Messages filter - only for users with OLJ access
      {
        key: "olj",
        label: "Direct Messages",
        icon: () =>
          h(
            "svg",
            {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 20 20",
              fill: "currentColor",
            },
            [
              h("path", {
                d: "M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z",
              }),
              h("path", {
                d: "M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z",
              }),
            ]
          ),
      },
    ].filter((option) => option.key !== "olj" || oljUnlocked.value));

    const handleResize = () => {
      isSmallScreen.value = window.innerWidth <= 599;
      isTablet.value = window.innerWidth > 599 && window.innerWidth <= 1024;

      // Reset thread panel visibility when switching to desktop view
      if (!isSmallScreen.value && !isTablet.value) {
        showThreadPanel.value = false;
      }
    };
    const triggerOljAutoSync = async () => {
      if (!oljUnlocked.value) return;
      try {
        await apiClient.get("/olj/auto-sync/");
      } catch {
        // silent — best-effort
      }
    };

    onMounted(() => {
      window.addEventListener("resize", handleResize);

      // Listen for thread archive events
      window.addEventListener("thread-archived", fetchThreads);

      // Fetch OLJ messages on mount if user is on OLJ filter
      if (filter.value === "olj") {
        if (oljMessages.value.length === 0) {
          fetchOljInboxMessages(1, oljMessagesPerPage.value, false);
        }
      }

      // Trigger background OLJ sync if data is stale
      triggerOljAutoSync();
    });
    onUnmounted(() => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("thread-archived", fetchThreads);
    });

    const fetchThreads = async () => {
      loading.value = true;
      const res = await apiClient.get("/messages/threads/");
      threads.value = Array.isArray(res.data)
        ? res.data
        : res.data.results || [];
      loading.value = false;
    };

    const fetchOljInboxMessages = async (
      page = 1,
      perPage = 20,
      append = false
    ) => {
      if (!oljUnlocked.value) return;
      // If appending, use loadingMore flag; otherwise use regular loading flag
      if (append) {
        oljMessagesLoadingMore.value = true;
      } else {
        oljMessagesLoading.value = true;
      }

      try {
        const res = await apiClient.get("/olj/messages/inbox/", {
          params: {
            page: page,
            per_page: perPage,
          },
        });


        // Handle the response structure: { data: [...], links: {...}, meta: {...} }
        if (res.data && res.data.data && Array.isArray(res.data.data)) {
          // Transform OLJ API response to thread-like format
          const transformedMessages = res.data.data.map((item) => {
            // Transform OLJ message format to thread format
            return {
              id: `olj_${item.thread.id}`,
              source: "olj",
              job_source: "olj",
              is_archived_by_jobseeker: item.thread.is_archived === 1,
              is_archived_by_admin: false,
              jobseeker_name: null,
              admin_name: "Employer",
              admin: null,
              jobseeker: userId.value,
              last_message: {
                id: item.thread.id,
                sender: null, // OLJ messages come from employers
                sender_name:
                  item.partner.name || item.partner.business_name || "Unknown",
                sender_account_type: "employer",
                body: item.thread.subject || "No subject",
                sent_at: item.latest_message_date,
                is_read: !item.unread_status,
              },
              // Additional OLJ-specific data
              olj_thread: item.thread,
              olj_partner: item.partner,
              olj_unread: item.unread_status,
              olj_latest_message_time_ago: item.latest_message_time_ago,
            };
          });

          // Append or replace messages based on append flag
          if (append) {
            oljMessages.value = [...oljMessages.value, ...transformedMessages];
          } else {
            oljMessages.value = transformedMessages;
          }

          // Store pagination metadata
          oljMessagesMeta.value = res.data.meta || null;
          oljMessagesLinks.value = res.data.links || null;
          oljMessagesPage.value = res.data.meta?.current_page || page;
        } else {
          // Handle empty or invalid response
          if (!append) {
            oljMessages.value = [];
          }
          oljMessagesMeta.value = null;
          oljMessagesLinks.value = null;
        }
      } catch (err) {
        console.error("Error fetching OLJ inbox messages:", err);
        if (!append) {
          oljMessages.value = [];
        }
        oljMessagesMeta.value = null;
        oljMessagesLinks.value = null;
        // Show error message to user
        if (err.response?.data?.error) {
          alert(`Error loading messages: ${err.response.data.error}`);
        } else if (err.message) {
          alert(`Error loading messages: ${err.message}`);
        }
      } finally {
        if (append) {
          oljMessagesLoadingMore.value = false;
        } else {
          oljMessagesLoading.value = false;
        }
      }
    };

    // Watch for user/auth state to be ready, then fetch threads
    watch(
      () => authStore.state.user,
      (user) => {
        if (!user) return;
        if (user.account_type === "admin") {
          loading.value = true;
          fetchThreads().finally(() => {
            loading.value = false;
          });
        } else {
          fetchThreads();
        }
      },
      { immediate: true }
    );

    // Watch for OLJ filter to fetch OLJ messages
    watch(
      () => filter.value,
      (newFilter, oldFilter) => {
        // Skip on initial watch (when oldFilter is undefined)
        if (oldFilter === undefined) {
          return;
        }

        // Only fetch if we're actually switching TO OLJ (not already on it)
        const isSwitchingToOlj = newFilter === "olj" && oldFilter !== "olj";

        if (isSwitchingToOlj) {
          oljMessagesPage.value = 1;
          fetchOljInboxMessages(1, oljMessagesPerPage.value, false);
        }
      },
      { immediate: false }
    );

    const filteredThreads = computed(() => {
      if (filter.value === "all") return threads.value;
      if (filter.value === "unread")
        return threads.value.filter(
          (t) => t.last_message && !t.last_message.is_read
        );
      if (filter.value === "sent") {
        if (!authStore.state.user) return [];
        return threads.value.filter(
          (t) =>
            t.last_message && t.last_message.sender === authStore.state.user.id
        );
      }
      if (filter.value === "archived") {
        if (isJobseeker.value)
          return threads.value.filter((t) => t.is_archived_by_jobseeker);
        return threads.value.filter((t) => t.is_archived_by_admin);
      }
      if (filter.value === "olj") {
        // OLJ messages will be handled in filteredAndSearchedThreads
        return [];
      }
      return threads.value;
    });

    const sortedThreads = computed(() =>
      [...filteredThreads.value].sort((a, b) => {
        const aTime = a.last_message
          ? new Date(a.last_message.sent_at).getTime()
          : 0;
        const bTime = b.last_message
          ? new Date(b.last_message.sent_at).getTime()
          : 0;
        return bTime - aTime;
      })
    );

    const goToThread = (id) => {
      router.push(`/inbox/thread/${id}`);
    };

    const composeThread = async () => {
      try {
        // Allow empty message
        const payload = {};
        if (composeBody.value && composeBody.value.trim()) {
          payload.first_message = composeBody.value;
        }
        const res = await apiClient.post("/messages/threads/", payload);
        showCompose.value = false;
        composeBody.value = "";
        // Go to the new thread page
        if (res.data && res.data.id) {
          router.push(`/inbox/thread/${res.data.id}`);
        } else {
          fetchThreads();
        }
      } catch (err) {
        console.error("Compose error:", err.response?.data || err);
        alert(
          "Error: " +
            (err.response?.data?.detail ||
              JSON.stringify(err.response?.data) ||
              err.message)
        );
      }
    };

    const formatDate = (dateStr) => {
      return new Date(dateStr).toLocaleString();
    };

    const unreadCount = computed(() => {
      const userId = authStore.state.user?.id;
      return threads.value.filter(
        (t) =>
          t.last_message &&
          !t.last_message.is_read &&
          t.last_message.sender !== userId // Only count if not sent by the logged-in user
      ).length;
    });

    const isArchivedThread = (thread) => {
      if (isJobseeker.value) {
        return thread.is_archived_by_jobseeker;
      }
      return thread.is_archived_by_admin;
    };

    const getEmptyStateMessage = () => {
      if (searchQuery.value) {
        if (isAdmin.value) {
          return `No results found for "${searchQuery.value}". Try searching by jobseeker username.`;
        }
        return `No results found for "${searchQuery.value}"`;
      }

      // Special message for OLJ filter
      if (filter.value === "olj") {
        if (oljMessagesLoading.value) {
          return "Loading messages...";
        }
        return "You don't have any messages";
      }

      switch (filter.value) {
        case "unread":
          return "You don't have any unread messages";
        case "sent":
          return "You haven't sent any messages yet";
        case "archived":
          return "You don't have any archived messages";
        case "olj":
          return "You don't have any messages";
        default:
          return "Your inbox is empty";
      }
    };

    const filteredAndSearchedThreads = computed(() => {
      let result = filteredThreads.value;

      // When OLJ filter is selected, show OLJ messages
      if (filter.value === "olj") {
        // Use OLJ messages from API - show all messages from OLJ API
        if (oljMessages.value.length > 0) {
          result = oljMessages.value;
        } else {
          // If still loading or no messages, return empty array to show loading state
          result = [];
        }
      }
      // When on unread filter, only show user messages (exclude OLJ threads)
      else if (filter.value === "unread") {
        result = result.filter((t) => !isOLJThread(t));
      }

      // Apply search filter if needed
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter((thread) => {
          // For admin users, search in jobseeker name and username
          if (isAdmin.value) {
            // Search in jobseeker name
            if (
              thread.jobseeker_name &&
              thread.jobseeker_name.toLowerCase().includes(query)
            ) {
              return true;
            }
            // Search in jobseeker username if available
            if (
              thread.jobseeker_username &&
              thread.jobseeker_username.toLowerCase().includes(query)
            ) {
              return true;
            }
            return false;
          } else {
            // For non-admin users, search in sender name
            if (
              thread.last_message &&
              thread.last_message.sender_name.toLowerCase().includes(query)
            ) {
              return true;
            }
            // Search in message body
            if (
              thread.last_message &&
              thread.last_message.body.toLowerCase().includes(query)
            ) {
              return true;
            }
            return false;
          }
        });
      }

      // Sort by latest message
      return result.sort((a, b) => {
        const aTime = a.last_message
          ? new Date(a.last_message.sent_at).getTime()
          : 0;
        const bTime = b.last_message
          ? new Date(b.last_message.sent_at).getTime()
          : 0;
        return bTime - aTime;
      });
    });

    const getOtherParticipantName = (thread) => {
      const user = authStore.state.user;
      if (!user) return "";
      if (user.account_type === "jobseeker") {
        // Use the actual admin's name from the thread
        return thread.admin_name || "Admin";
      } else {
        return (
          thread.jobseeker_name ||
          (thread.last_message && thread.last_message.sender_name) ||
          "User"
        );
      }
    };
    const getOtherParticipantInitial = (thread) => {
      if (thread.source === "olj" && thread.olj_partner) {
        const name =
          thread.olj_partner.name ||
          thread.olj_partner.business_name ||
          "Employer";
        return name.charAt(0).toUpperCase();
      }
      const name = getOtherParticipantName(thread);
      return name ? name.charAt(0).toUpperCase() : "?";
    };

    const createSupportThread = async () => {
      try {
        const res = await apiClient.post("/messages/threads/", {});
        if (res.data && res.data.id) {
          router.push(`/inbox/thread/${res.data.id}`);
        } else {
          fetchThreads();
        }
      } catch (err) {
        console.error("Create thread error:", err.response?.data || err);
        alert(
          "Error: " +
            (err.response?.data?.detail ||
              JSON.stringify(err.response?.data) ||
              err.message)
        );
      }
    };

    const fetchOljThreadDetails = async (hashId, page = 1, perPage = 20) => {
      if (!oljUnlocked.value) return;
      oljThreadLoading.value = true;
      oljThreadData.value = null;
      try {
        const res = await apiClient.get(`/olj/messages/thread/${hashId}/`, {
          params: {
            page: page,
            per_page: perPage,
          },
        });
        oljThreadData.value = res.data;
      } catch (err) {
        console.error("Error fetching OLJ thread details:", err);
        if (err.response?.data?.error) {
          alert(`Error loading thread: ${err.response.data.error}`);
        } else {
          alert(`Error loading thread: ${err.message || "Unknown error"}`);
        }
        oljThreadData.value = null;
      } finally {
        oljThreadLoading.value = false;
      }
    };

    const selectThread = async (id) => {
      // If it's an OLJ message, fetch and display the thread details
      if (id.toString().startsWith("olj_")) {
        const oljThreadId = id.replace("olj_", "");
        // Find the thread to get the hash_id
        const oljThread = oljMessages.value.find(
          (msg) => msg.olj_thread?.id.toString() === oljThreadId
        );
        // Use hash_id if available, otherwise use thread id
        const threadHashId = oljThread?.olj_thread?.hash_id || oljThreadId;
        if (threadHashId) {
          selectedOljHashId.value = threadHashId;
          selectedThreadId.value = null; // Clear regular thread selection
          // Store partner data for display
          selectedOljPartner.value = oljThread?.olj_partner || null;
          await fetchOljThreadDetails(threadHashId);
          // Show the thread panel on both mobile and tablet views
          if (isSmallScreen.value || isTablet.value) {
            showThreadPanel.value = true;
          }
        }
        return;
      }

      // Regular thread selection
      selectedThreadId.value = id;
      selectedOljHashId.value = null; // Clear OLJ thread selection
      selectedOljPartner.value = null; // Clear OLJ partner data
      oljThreadData.value = null;
      // Show the thread panel on both mobile and tablet views
      if (isSmallScreen.value || isTablet.value) {
        showThreadPanel.value = true;
      }
    };

    // Helper function to check if an OLJ thread is selected
    const isOljThreadSelected = (thread) => {
      if (thread.source !== "olj" || !selectedOljHashId.value) return false;
      const threadHashId =
        thread.olj_thread?.hash_id || thread.olj_thread?.id?.toString();
      return threadHashId === selectedOljHashId.value;
    };

    const formatThreadDate = (dateStr) => {
      const date = new Date(dateStr);
      const now = new Date();
      if (
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      ) {
        // Show only time
        return date.toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        });
      }
      // Show day of week and time
      const day = date.toLocaleDateString([], { weekday: "short" });
      const time = date.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
      return `${day} ${time}`;
    };

    const showNewThreadModal = ref(false);
    const jobseekerList = ref([]);
    const selectedJobseeker = ref("");
    const creatingThread = ref(false);
    const threadError = ref("");

    // Message Blast Modal Variables
    const showMessageBlastModal = ref(false);
    const allUsers = ref([]);
    const selectedUserIds = ref([]);
    const userSearchQuery = ref("");
    const blastMessage = ref("");
    const loadingUsers = ref(false);
    const sendingBlast = ref(false);
    const blastError = ref("");
    const blastSuccess = ref("");
    const adminDisplayName = computed(() => {
      const user = authStore.state.user;
      return user
        ? `${user.first_name} ${user.last_name}`.trim() || user.username
        : "";
    });
    const adminDisplayEmail = computed(() => {
      const user = authStore.state.user;
      return user ? user.email : "";
    });
    const adminId = computed(() => authStore.state.user?.id);
    const selectedJobseekerObj = computed(() =>
      jobseekerList.value.find((js) => js.id === selectedJobseeker.value)
    );

    // Fetch jobseekers without thread
    const fetchJobseekers = async () => {
      if (!isAdmin.value) return;
      try {
        const jsRes = await apiClient.get(
          "/messages/jobseekers-without-thread/"
        );
        jobseekerList.value = Array.isArray(jsRes.data) ? jsRes.data : [];
        if (jobseekerList.value.length > 0)
          selectedJobseeker.value = jobseekerList.value[0].id;
      } catch (err) {
        threadError.value = "Failed to load jobseeker list.";
      }
    };
    watch(showNewThreadModal, (val) => {
      if (val) fetchJobseekers();
    });

    // Create new thread
    const createNewThread = async () => {
      threadError.value = "";
      if (!selectedJobseeker.value) {
        threadError.value = "Please select a jobseeker.";
        return;
      }
      creatingThread.value = true;
      try {
        const res = await apiClient.post("/messages/threads/", {
          admin: adminId.value,
          jobseeker: selectedJobseeker.value,
        });
        showNewThreadModal.value = false;
        creatingThread.value = false;
        // Refresh threads
        await fetchThreads();
        // Optionally, auto-select the new thread
        if (res.data && res.data.id) {
          selectThread(res.data.id);
        }
      } catch (err) {
        threadError.value =
          err.response?.data?.detail || "Failed to create thread.";
        creatingThread.value = false;
      }
    };

    // Message Blast Functions
    const filteredUsers = computed(() => {
      if (!userSearchQuery.value.trim()) {
        return allUsers.value;
      }
      const query = userSearchQuery.value.toLowerCase();
      return allUsers.value.filter(
        (user) =>
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query) ||
          user.account_type.toLowerCase().includes(query)
      );
    });

    const fetchAllUsers = async () => {
      if (!isAdmin.value) return;
      loadingUsers.value = true;
      try {
        const response = await apiClient.get("/messages/all-users-for-blast/");
        allUsers.value = Array.isArray(response.data) ? response.data : [];
      } catch (err) {
        blastError.value = "Failed to load users.";
        console.error("Error fetching users:", err);
      } finally {
        loadingUsers.value = false;
      }
    };

    const selectAllUsers = () => {
      selectedUserIds.value = filteredUsers.value.map((user) => user.id);
    };

    const clearAllUsers = () => {
      selectedUserIds.value = [];
    };

    const sendMessageBlast = async () => {
      blastError.value = "";
      if (selectedUserIds.value.length === 0) {
        blastError.value = "Please select at least one user.";
        return;
      }
      if (!blastMessage.value.trim()) {
        blastError.value = "Please enter a message.";
        return;
      }

      sendingBlast.value = true;
      blastError.value = "";
      blastSuccess.value = "";

      try {
        const response = await apiClient.post("/messages/send-blast/", {
          user_ids: selectedUserIds.value,
          message: blastMessage.value.trim(),
        });

        // Show success message
        blastSuccess.value = `Message blast sent successfully to ${response.data.details.total_users} users!`;

        // Reset form after a short delay
        setTimeout(() => {
          showMessageBlastModal.value = false;
          selectedUserIds.value = [];
          blastMessage.value = "";
          userSearchQuery.value = "";
          blastSuccess.value = "";
        }, 2000);

        // Refresh threads to show new conversations
        await fetchThreads();
      } catch (err) {
        blastError.value =
          err.response?.data?.detail || "Failed to send message blast.";
        console.error("Error sending message blast:", err);
      } finally {
        sendingBlast.value = false;
      }
    };

    // Watch for modal opening to fetch users
    watch(showMessageBlastModal, (val) => {
      if (val) {
        fetchAllUsers();
        // Reset form when opening
        selectedUserIds.value = [];
        blastMessage.value = "";
        userSearchQuery.value = "";
        blastError.value = "";
        blastSuccess.value = "";
      }
    });

    const searchPlaceholder = computed(() => {
      return isAdmin.value
        ? "Search by username..."
        : "Search conversations...";
    });

    // Method to highlight matching search text
    const highlightSearchMatch = (text) => {
      if (!searchQuery.value.trim() || !text) return text;

      const query = searchQuery.value.trim().toLowerCase();
      const lowerText = text.toLowerCase();

      if (lowerText.includes(query)) {
        const regex = new RegExp(`(${escapeRegExp(query)})`, "gi");
        return text.replace(
          regex,
          '<span class="bg-yellow-200 text-gray-900 font-semibold px-1 rounded">$1</span>'
        );
      }

      return text;
    };

    // Helper to escape special regex characters
    const escapeRegExp = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };

    // Method to get sidebar style to avoid ESLint errors with complex binding
    const getSidebarStyle = () => {
      if (isSmallScreen.value) {
        if (mobileMenuOpen.value) {
          return "transform: translateX(0); background: rgba(255,255,255,0.98); top: 56px; height: calc(100vh - 56px);";
        } else {
          return "transform: translateX(-100vw); top: 56px; height: calc(100vh - 56px);";
        }
      }
      return "";
    };

    // In the script section, add this method to check for admin threads
    const isAdminParticipant = (thread) => {
      // If user is jobseeker, check if the thread has an admin
      if (authStore.state.user?.account_type === "jobseeker") {
        return thread.admin !== null;
      }
      return false;
    };

    const isMessageFromAdmin = (message) => {
      if (!message) return false;
      return message.sender_account_type === "admin";
    };

    // Heuristic to detect OLJ-related threads without backend changes
    const isOLJThread = (thread) => {
      if (!thread) return false;
      // Prefer explicit source fields if present
      if (
        typeof thread.source === "string" &&
        thread.source.toLowerCase() === "olj"
      )
        return true;
      if (
        typeof thread.job_source === "string" &&
        thread.job_source.toLowerCase() === "olj"
      )
        return true;
      // Fall back to name hints
      const names = [
        thread.jobseeker_name,
        thread.admin_name,
        thread.last_message?.sender_name,
      ]
        .filter(Boolean)
        .map((n) => String(n).toLowerCase());
      return names.some((n) => n.includes("olj") || n.includes("onlinejobs"));
    };

    const unreadUsersCount = computed(() => {
      return filteredThreads.value.filter(
        (t) => t.last_message && !t.last_message.is_read && !isOLJThread(t)
      ).length;
    });

    const unreadOljCount = computed(() => {
      // If we have OLJ messages loaded, use those for count
      if (oljMessages.value.length > 0) {
        return oljMessages.value.filter((msg) => msg.olj_unread).length;
      }
      // Otherwise fallback to filtered threads
      return filteredThreads.value.filter(
        (t) => t.last_message && !t.last_message.is_read && isOLJThread(t)
      ).length;
    });

    // Handle infinite scroll for OLJ messages
    const handleOljScroll = () => {
      // Only handle scroll for OLJ messages
      if (filter.value !== "olj") {
        return;
      }

      if (!conversationsListRef.value) return;
      if (oljMessagesLoading.value || oljMessagesLoadingMore.value) return;

      const container = conversationsListRef.value;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Check if we're near the bottom (within 100px)
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      if (isNearBottom) {
        // Check if there's a next page available
        const hasNextPage =
          oljMessagesLinks.value?.next ||
          (oljMessagesMeta.value &&
            oljMessagesMeta.value.current_page <
              oljMessagesMeta.value.last_page);

        if (hasNextPage && !oljMessagesLoadingMore.value) {
          const nextPage = oljMessagesPage.value + 1;
          fetchOljInboxMessages(nextPage, oljMessagesPerPage.value, true);
        }
      }
    };

    return {
      threads,
      loading,
      filter,
      showCompose,
      composeBody,
      isJobseeker,
      filteredThreads,
      sortedThreads,
      searchQuery,
      searchPlaceholder,
      highlightSearchMatch,
      getSidebarStyle,
      mobileMenuOpen,
      filteredAndSearchedThreads,
      isArchivedThread,
      getEmptyStateMessage,
      goToThread,
      composeThread,
      formatDate,
      unreadCount,
      getOtherParticipantName,
      getOtherParticipantInitial,
      createSupportThread,
      selectedThreadId,
      selectThread,
      showThreadPanel,
      isSmallScreen,
      formatThreadDate,
      userId,
      isAdmin,
      filterOptions,
      showNewThreadModal,
      jobseekerList,
      selectedJobseeker,
      selectedJobseekerObj,
      creatingThread,
      threadError,
      createNewThread,
      adminDisplayName,
      adminDisplayEmail,
      isTablet,
      isAdminParticipant,
      isMessageFromAdmin,
      // Message Blast
      showMessageBlastModal,
      allUsers,
      selectedUserIds,
      userSearchQuery,
      blastMessage,
      loadingUsers,
      sendingBlast,
      blastError,
      blastSuccess,
      filteredUsers,
      fetchAllUsers,
      selectAllUsers,
      clearAllUsers,
      sendMessageBlast,
      // Unread counts
      unreadUsersCount,
      unreadOljCount,
      // OLJ messages
      oljMessages,
      oljMessagesLoading,
      oljMessagesLoadingMore,
      oljMessagesMeta,
      oljMessagesLinks,
      fetchOljInboxMessages,
      // Infinite scroll
      conversationsListRef,
      handleOljScroll,
      // OLJ thread
      oljThreadData,
      oljThreadLoading,
      selectedOljHashId,
      fetchOljThreadDetails,
      selectedOljPartner,
      isOljThreadSelected,
    };
  },
};
</script>

<style scoped>
/* Modern Inbox Design */
.inbox-page-container {
  min-height: 100vh;
  position: relative;
  background: #f8fafc;
}

.background-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  z-index: 0;
  pointer-events: none;
}

/* Search Input */
.search-input {
  transition: all 0.2s ease;
}

.search-input:focus {
  background: white;
  box-shadow: 0 0 0 3px hsl(var(--ring) / 0.1);
}

/* Thread Cards */
.thread-card {
  transition: all 0.2s ease;
}

.thread-card:hover {
  background: #f9fafb !important;
}

.thread-card.active {
  background: #eff6ff;
  border-left: 4px solid #4a6bfd;
}

/* Filter Tabs */
.filter-tabs {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.filter-tabs::-webkit-scrollbar {
  display: none;
}

.filter-tab {
  position: relative;
}

.filter-tab:active {
  transform: scale(0.98);
}

/* Utility Classes */
.badge-count {
  font-size: 0.7rem;
  line-height: 1;
}

/* Conversations List */
.conversations-list::-webkit-scrollbar {
  width: 6px;
}

.conversations-list::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.conversations-list::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 3px;
}

.conversations-list::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}

.thread-list {
  display: flex;
  flex-direction: column;
}

/* Responsive Design */
@media (max-width: 1023px) {
  .sidebar-panel {
    width: 100% !important;
    max-width: 100vw;
  }

  .conversations-panel {
    height: calc(100vh - 56px) !important;
    height: calc(100dvh - 56px) !important;
  }

  .thread-panel {
    height: calc(100vh - 56px) !important;
    height: calc(100dvh - 56px) !important;
  }
}

/* Modal Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Search Highlight */
.preview-text :deep(span.bg-yellow-200),
.thread-title-text :deep(span.bg-yellow-200) {
  background-color: #fef08a !important;
  color: #1e293b !important;
}

/* Primary color override for buttons */
.bg-primary {
  background-color: #4a6bfd;
}

.bg-primary-dark {
  background-color: #3a56d4;
}

.text-primary {
  color: #4a6bfd;
}

.ring-primary {
  --tw-ring-color: #4a6bfd;
}

/* Modal Styles */
.modal-responsive {
  max-width: 700px;
  width: 100%;
}

@media (max-width: 640px) {
  .modal-responsive {
    max-width: 95vw;
    margin: 0 10px;
  }
  .modal-header {
    padding: 15px;
  }
  .modal-body {
    padding: 16px;
  }
}

/* Mobile Responsive */
@media (max-width: 599px) {
  .sidebar-panel {
    width: 85vw !important;
    max-width: 400px;
  }

  .conversations-list {
    height: calc(100vh - 200px) !important;
    height: calc(100dvh - 200px) !important;
  }

  .thread-card {
    padding: 1rem !important;
  }
}

/* Desktop adjustments */
@media (min-width: 1024px) {
  .conversations-list {
    height: calc(100vh - 220px) !important;
  }
}
</style>
